import {
  FirestoreDataConverter,
  DocumentSnapshot,
  SnapshotOptions,
  WithFieldValue,
} from "firebase/firestore";
import { ExpectedExpense, normalizeDiscountType, normalizeDiscountValue } from "../expenses";
import { dateToNYIsoString } from "./expenseConverter";

/** Normalize a list of attachment rotations to the nearest 0/90/180/270 degree value. */
const sanitizeAttachmentRotations = (value: unknown): number[] | null => {
  if (!Array.isArray(value)) return null;
  const out: number[] = [];
  for (const r of value) {
    if (typeof r === "number" && Number.isFinite(r)) {
      out.push((((Math.round(r / 90) * 90) % 360) + 360) % 360);
    }
  }
  return out;
};

const parseDate = (dateValue: any): Date => {
  if (!dateValue) return new Date();
  try {
    if (dateValue && typeof dateValue === "object" && "toDate" in dateValue) {
      return dateValue.toDate();
    }
    if (dateValue instanceof Date) return dateValue;
    if (typeof dateValue === "string") return new Date(dateValue);
    return new Date();
  } catch {
    return new Date();
  }
};

const safeNumber = (value: any): number => {
  if (value === undefined || value === null) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const expectedExpenseConverter: FirestoreDataConverter<ExpectedExpense> = {
  toFirestore(expected: WithFieldValue<ExpectedExpense>) {
    const { id, ...data } = expected;

    return {
      ...data,
      category: data.category || "Repairs",
      amount: typeof data.amount === "number" && !Number.isNaN(data.amount) ? data.amount : 0,
      description: data.description || "",
      date: data.date instanceof Date ? dateToNYIsoString(data.date) : data.date,
      customCategory: data.customCategory || null,
      businessName: data.businessName || null,
      invoiceNumber: data.invoiceNumber || null,
      vendorCategory: data.vendorCategory || null,
      vehicleId: data.vehicleId || null,
      repairShopId: data.repairShopId || null,
      repairTruckId: data.repairTruckId || null,
      sourceRepairEstimateStoragePath: data.sourceRepairEstimateStoragePath || null,
      sourceRepairTruckId: data.sourceRepairTruckId || null,
      pendingInvoiceStoragePath: data.pendingInvoiceStoragePath || null,
      attachmentPath: data.attachmentPath || null,
      attachmentURL: data.attachmentURL || null,
      attachmentPaths: Array.isArray(data.attachmentPaths)
        ? data.attachmentPaths.filter((path): path is string => typeof path === "string" && path.length > 0)
        : null,
      attachmentURLs: Array.isArray(data.attachmentURLs)
        ? data.attachmentURLs.filter((url): url is string => typeof url === "string" && url.length > 0)
        : null,
      attachmentRotations: sanitizeAttachmentRotations(data.attachmentRotations),
      repairSubcategories: Array.isArray(data.repairSubcategories) ? data.repairSubcategories : null,
      customRepairSubcategory: data.customRepairSubcategory || null,
      repairPartsAmount: typeof data.repairPartsAmount === "number" ? data.repairPartsAmount : null,
      repairLaborAmount: typeof data.repairLaborAmount === "number" ? data.repairLaborAmount : null,
      repairFeesAmount: typeof data.repairFeesAmount === "number" ? data.repairFeesAmount : null,
      repairTaxAmount: typeof data.repairTaxAmount === "number" ? data.repairTaxAmount : null,
      discountType: normalizeDiscountType(data.discountType),
      discountValue: normalizeDiscountValue(
        typeof data.discountValue === "number" ? data.discountValue : null,
        normalizeDiscountType(data.discountType),
      ),
      lineItems: Array.isArray(data.lineItems) ? data.lineItems : null,
      reviewStatus: data.reviewStatus || null,
      awaitingExtraction: data.awaitingExtraction === true,
      extractedData: data.extractedData ?? null,
      extractionModel: data.extractionModel || null,
      extractionError: data.extractionError || null,
      suggestedCategory: data.suggestedCategory || null,
      created_by: data.created_by || "",
      created_at: data.created_at instanceof Date ? dateToNYIsoString(data.created_at) : data.created_at,
      updated_at: data.updated_at instanceof Date ? dateToNYIsoString(data.updated_at) : data.updated_at,
      updated_by: data.updated_by || null,
    };
  },
  fromFirestore(snapshot: DocumentSnapshot, options: SnapshotOptions): ExpectedExpense {
    const data = snapshot.data(options) ?? {};

    const category =
      typeof data.category === "string" && data.category.length > 0 ? data.category : "Repairs";

    return {
      id: snapshot.id,
      category,
      amount: safeNumber(data.amount),
      description: data.description || "",
      date: parseDate(data.date),
      customCategory: category === "Other" ? data.customCategory || null : null,
      businessName: data.businessName || null,
      invoiceNumber: data.invoiceNumber || null,
      vendorCategory: data.vendorCategory || null,
      vehicleId: data.vehicleId || null,
      repairShopId: data.repairShopId || null,
      repairTruckId: data.repairTruckId || null,
      sourceRepairEstimateStoragePath: data.sourceRepairEstimateStoragePath || null,
      sourceRepairTruckId: data.sourceRepairTruckId || null,
      pendingInvoiceStoragePath: data.pendingInvoiceStoragePath || null,
      attachmentPath: data.attachmentPath || null,
      attachmentURL: data.attachmentURL || null,
      attachmentPaths: Array.isArray(data.attachmentPaths)
        ? data.attachmentPaths.filter((path: unknown): path is string => typeof path === "string" && path.length > 0)
        : null,
      attachmentURLs: Array.isArray(data.attachmentURLs)
        ? data.attachmentURLs.filter((url: unknown): url is string => typeof url === "string" && url.length > 0)
        : null,
      attachmentRotations: sanitizeAttachmentRotations(data.attachmentRotations),
      repairSubcategories: Array.isArray(data.repairSubcategories) ? data.repairSubcategories : null,
      customRepairSubcategory: data.customRepairSubcategory || null,
      repairPartsAmount: typeof data.repairPartsAmount === "number" ? data.repairPartsAmount : null,
      repairLaborAmount: typeof data.repairLaborAmount === "number" ? data.repairLaborAmount : null,
      repairFeesAmount: typeof data.repairFeesAmount === "number" ? data.repairFeesAmount : null,
      repairTaxAmount: typeof data.repairTaxAmount === "number" ? data.repairTaxAmount : null,
      discountType: normalizeDiscountType(data.discountType),
      discountValue: normalizeDiscountValue(
        typeof data.discountValue === "number" ? data.discountValue : null,
        normalizeDiscountType(data.discountType),
      ),
      lineItems: Array.isArray(data.lineItems)
        ? (data.lineItems as Array<Record<string, unknown>>).map((li) => ({
            description: typeof li.description === "string" ? li.description : "",
            amount: typeof li.amount === "number" ? li.amount : null,
            quantity:
              typeof li.quantity === "number" && Number.isFinite(li.quantity) ? li.quantity : null,
            subcategory:
              typeof li.subcategory === "string" && li.subcategory.length > 0 ? li.subcategory : null,
            repairGroup: typeof li.repairGroup === "string" ? li.repairGroup : null,
          }))
        : null,
      reviewStatus: data.reviewStatus || null,
      awaitingExtraction: data.awaitingExtraction === true ? true : null,
      extractedData: (data.extractedData as Record<string, unknown> | null) ?? null,
      extractionModel: data.extractionModel || null,
      extractionError: data.extractionError || null,
      suggestedCategory: data.suggestedCategory || null,
      created_by: data.created_by || "",
      created_at: parseDate(data.created_at),
      updated_at: data.updated_at ? parseDate(data.updated_at) : null,
      updated_by: data.updated_by || null,
    };
  },
};

export { expectedExpenseConverter };
